import type { ReactNode } from 'react'

import Link from 'next/link'
import { LogoMark } from '@/components/generals/logo'
import { TRADING_HOURS, siteConfig } from '@/constants/site'

interface SiteFooterProps {
    year: string
}

const COLUMNS = [
    {
        title: 'Markets',
        links: [
            { href: '/', label: 'Market overview' },
            { href: '/equities', label: 'Equities' },
            { href: '/indices', label: 'Indices' },
            { href: '/bonds', label: 'Bonds and bills' }
        ]
    },
    {
        title: 'Analytics',
        links: [
            { href: '/statistics', label: 'Statistics' },
            { href: '/flows', label: 'Investor flows' },
            { href: '/calculators', label: 'Calculators' },
            { href: '/equities?view=technicals', label: 'Technical screen' }
        ]
    },
    {
        title: 'Exchange',
        links: [
            { href: '/announcements', label: 'Announcements' },
            { href: '/#how-trading-works', label: 'How trading works' },
            { href: '/indices#methodology', label: 'Index methodology' },
            { href: '/calculators#trade-cost', label: 'Fees and levies' }
        ]
    }
]

const SiteFooter = ({ year }: SiteFooterProps): ReactNode => {
    return (
        <footer className='mt-auto border-t border-border bg-card/40'>
            <div className='mx-auto grid max-w-[1440px] gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.4fr_2fr]'>
                <div className='max-w-md'>
                    <div className='flex items-center gap-2.5'>
                        <LogoMark className='size-8' />
                        <span className='font-heading text-base font-semibold tracking-tight'>{siteConfig.name}</span>
                    </div>

                    <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                        Prices, indices, statistics and capital flows for Kenyan listed companies, with the arithmetic behind every number one click away.
                    </p>

                    <dl className='mt-6 grid gap-2 text-[13px]'>
                        {TRADING_HOURS.map(item => (
                            <div key={item.label} className='flex justify-between gap-4 border-b border-border/70 pb-2'>
                                <dt className='text-muted-foreground'>{item.label}</dt>
                                <dd className='tabular-nums text-foreground'>{item.value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='grid gap-8 sm:grid-cols-3'>
                    {COLUMNS.map(column => (
                        <nav key={column.title} aria-label={column.title}>
                            <h2 className='text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground'>{column.title}</h2>
                            <ul className='mt-3 space-y-2'>
                                {column.links.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className='text-sm text-foreground/85 transition-colors hover:text-primary'>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}
                </div>
            </div>

            <div id='data-notice' className='border-t border-border'>
                <div className='mx-auto flex max-w-[1440px] flex-col gap-3 px-4 py-6 text-xs leading-relaxed text-muted-foreground sm:px-6 md:flex-row md:items-start md:justify-between'>
                    <p className='max-w-3xl'>
                        <span className='font-medium text-foreground'>Data notice. </span>
                        {siteConfig.disclaimer}
                    </p>
                    <p className='shrink-0'>
                        &copy; {year} {siteConfig.name}. Nairobi, Kenya.
                    </p>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
